import React from "react";
import {
  SelectField,
  BooleanField,
  ShowController,
  ShowView,
  Pagination,
  Datagrid,
  ReferenceManyField,
  Tab,
  TabbedShowLayout,
  FunctionField,
  ReferenceField,
  Button,
  EditButton,
  TopToolbar,
  SimpleShowLayout,
  TextField
} from "react-admin";
import { Link } from "react-router-dom";
import CreateIcon from "@material-ui/icons/Add";
import RollupIcon from "@material-ui/icons/ArrowUpward";
import dataProvider from "../data/providers/unionDataProvider";
import FootballTab from "./FootballTab";
import GirlsVolleyballTab from "./GirlsVolleyballTab";
import BasketballTab from "./BasketballTab";
import SoccerTab from "./SoccerTab";
import BaseballTab from "./BaseballTab";
import SeasonTeamTitle from "./SeasonTeamTitle";
import _ from "lodash";
import { CustomGameStatusField } from "../fields/CustomGamesStatusField";

async function rollupTeamAction(data) {
  if (window.confirm("Really recalculate rollup?")) {
    await dataProvider
      .getBaseProvider()
      .providers.seasonTeams.rollup(null, { id: data.id });
  }
}

async function rollupPlayersAction(data) {
  if (window.confirm("Really recalculate rollup?")) {
    await dataProvider
      .getBaseProvider()
      .providers.seasonTeams.rollupPlayers(null, { id: data.id });
  }
}

const getPlayerCreateState = (data) => {
  const { seasonCode, sportCode, team, id } = data;
  const schoolName = _.get(team, "school.name");
  const players = _.get(data, "players", []).map((val) => val?.person?.name);
  return {
    teamInfo: `${schoolName} | ${sportCode} | ${seasonCode}`,
    record: {
      season_team_id: id,
      sportCode: sportCode
    },
    players
  };
};

const SeasonTeamShowActions = ({ basePath, data = {}, resource }) => {
  return (
    <TopToolbar>
      <EditButton basePath={basePath} record={data} />
      {data && (
        <React.Fragment>
          <Button
            label="Add Player"
            component={Link}
            to={{
              pathname: "/players/create",
              state: getPlayerCreateState(data)
            }}
          >
            <CreateIcon />
          </Button>
          <Button label="Rollup Team" onClick={(_) => rollupTeamAction(data)}>
            <RollupIcon />
          </Button>
          <Button
            label="Rollup Players"
            onClick={(_) => rollupPlayersAction(data)}
          >
            <RollupIcon />
          </Button>
        </React.Fragment>
      )}
    </TopToolbar>
  );
};

function districtLabel(r) {
  return (
    [
      r.leagueCode,
      r.conferenceCode,
      r.divisionCode,
      r.regionCode,
      r.districtCode
    ].join(" ") +
    "(" +
    r.id +
    ")"
  );
}

const SeasonTeamShow = (props) => {
  return (
    <ShowController {...props}>
      {(controllerProps) => (
        <ShowView
          actions={<SeasonTeamShowActions />}
          title={<SeasonTeamTitle />}
          {...props}
          {...controllerProps}
        >
          <TabbedShowLayout>
            <Tab label="summary">
              <SimpleShowLayout>
                <TextField source="id" />
                <ReferenceField label="Team" source="team.id" reference="teams">
                  <TextField source="name" />
                </ReferenceField>
                <TextField source="seasonCode" />
                <TextField source="sportCode" />
                <BooleanField source="isInArea" />
                <TextField source="coachName" />
                <TextField source="coachEmail" />
                <TextField source="coachUpdated" />
                <SelectField
                  source="rollupMode"
                  choices={[
                    { id: "AUTOMATIC", name: "AUTOMATIC" },
                    { id: "MANUAL", name: "MANUAL" }
                  ]}
                />
                <ReferenceField
                  label="District"
                  source="district.id"
                  reference="districts"
                >
                  <FunctionField label="District" render={districtLabel} />
                </ReferenceField>
              </SimpleShowLayout>
            </Tab>
            {controllerProps.record &&
              controllerProps.record.sportCode === "football" && (
                <Tab label="football">
                  <FootballTab />
                </Tab>
              )}
            {controllerProps.record &&
              controllerProps.record.sportCode === "girls-volleyball" && (
                <Tab label="volleyball">
                  <GirlsVolleyballTab />
                </Tab>
              )}
            {controllerProps.record &&
              controllerProps.record.sportCode === "girls-basketball" && (
                <Tab label="girls-basketball">
                  <BasketballTab />
                </Tab>
              )}
            {controllerProps.record &&
              controllerProps.record.sportCode === "boys-basketball" && (
                <Tab label="boys-basketball">
                  <BasketballTab />
                </Tab>
              )}
            {controllerProps.record &&
              controllerProps.record.sportCode === "girls-soccer" && (
                <Tab label="girls-soccer">
                  <SoccerTab />
                </Tab>
              )}
            {controllerProps.record &&
              controllerProps.record.sportCode === "boys-soccer" && (
                <Tab label="boys-soccer">
                  <SoccerTab />
                </Tab>
              )}
            {controllerProps.record &&
              controllerProps.record.sportCode === "softball" && (
                <Tab label="softball">
                  <BaseballTab />
                </Tab>
              )}
            {controllerProps.record &&
              controllerProps.record.sportCode === "baseball" && (
                <Tab label="baseball">
                  <BaseballTab />
                </Tab>
              )}
            <Tab label="players">
              <SimpleShowLayout>
                <ReferenceManyField
                  reference="players"
                  target="school_sport_season_team_id"
                  addLabel={false}
                  pagination={<Pagination />}
                  fullWidth
                >
                  <Datagrid rowClick="show">
                    <TextField source="id" />
                    <TextField source="person.name" label="Name" />
                    <TextField source="jerseyNumber" />
                    <TextField source="position" />
                    <TextField source="yearCode" />
                    <TextField source="isActive" />
                  </Datagrid>
                </ReferenceManyField>
              </SimpleShowLayout>
            </Tab>
            <Tab label="games">
              <SimpleShowLayout>
                <ReferenceManyField
                  reference="gamestats"
                  target="school_sport_season_team_id"
                  source="id"
                  addLabel={false}
                  pagination={<Pagination />}
                  fullWidth
                >
                  <Datagrid rowClick="show">
                    <TextField source="id" />
                    <TextField label="date" source="gameDateString" />
                    <TextField
                      label="home"
                      source="home.season.team.school.name"
                    />
                    <TextField
                      label="away"
                      source="away.season.team.school.name"
                    />
                    <CustomGameStatusField
                      source="gameStatus"
                      label="Is Final"
                    />
                  </Datagrid>
                </ReferenceManyField>
              </SimpleShowLayout>
            </Tab>
          </TabbedShowLayout>
        </ShowView>
      )}
    </ShowController>
  );
};

export default SeasonTeamShow;
